// The typed reading of a schedule rule, in words (wireframe 4's "→" line):
// what the rule's columns say, so a reviewer can check it against the quote
// above it. Nothing here computes a date: Mint does.
import { cmDate } from './format';
export const RULE_KINDS = [
    {
        value: 'recurring',
        label: 'Recurring'
    },
    {
        value: 'dated',
        label: 'Once'
    },
    {
        value: 'event',
        label: 'After an event'
    }
];
export const RULE_GATES = [
    {
        value: 'base',
        label: 'Base period'
    },
    {
        value: 'option_period',
        label: 'An option period'
    },
    {
        value: 'optional_task',
        label: 'An optional task'
    },
    {
        value: 'none',
        label: 'Whenever the event happens'
    }
];
export const OFFSET_UNITS = [
    {
        value: 'calendar_day',
        label: 'days'
    },
    {
        value: 'business_day',
        label: 'business days'
    },
    {
        value: 'week',
        label: 'weeks'
    },
    {
        value: 'month',
        label: 'months'
    }
];
export const CADENCES = [
    {
        value: 'weekly',
        label: 'Weekly'
    },
    {
        value: 'biweekly',
        label: 'Every two weeks'
    },
    {
        value: 'semimonthly',
        label: 'Twice a month'
    },
    {
        value: 'monthly',
        label: 'Monthly'
    },
    {
        value: 'quarterly',
        label: 'Quarterly'
    },
    {
        value: 'semiannually',
        label: 'Twice a year'
    },
    {
        value: 'annually',
        label: 'Yearly'
    },
    {
        value: 'daily',
        label: 'Daily'
    }
];
/** Events a reviewer logs (never the contract's own native ones). */ export const EVENT_KINDS = [
    {
        value: 'receipt_of_order',
        label: 'Receipt of order (ARO)'
    },
    {
        value: 'exercise_start',
        label: 'Each exercise starts'
    },
    {
        value: 'option_period_start',
        label: 'Each option period starts'
    },
    {
        value: 'award',
        label: 'The award'
    },
    {
        value: 'modification_effective',
        label: 'A modification takes effect'
    },
    {
        value: 'on_request',
        label: 'The government asks for it'
    }
];
/** Read from the contract's own rows (or a government ask): never logged by hand here. */ const NOT_LOGGED_HERE = new Set([
    'award',
    'exercise_start',
    'option_period_start',
    'modification_effective',
    'on_request'
]);
/**
 * The events a person can log: every kind a rule waits on or a deliverable
 * dates (S6), in label order. Each reads as the deliverable that dates it
 * ("Technical Kickoff Meeting") when there is one.
 */ export function loggableEvents(rules, duties) {
    const named = new Map();
    for (const d of duties)if (d.eventKind) named.set(d.eventKind, d.title);
    const kinds = new Set(named.keys());
    for (const r of rules)if (r.eventKind && !NOT_LOGGED_HERE.has(r.eventKind)) kinds.add(r.eventKind);
    return [
        ...kinds
    ].map((kind)=>({
            kind,
            label: eventLabel(kind, named.get(kind))
        })).sort((a, b)=>a.label.localeCompare(b.label));
}
/**
 * The events deliverables are waiting on (their `waiting_on_event` check),
 * from ListDuties: each such deliverable's event rules name the kinds. As on
 * pangaea, receipt of order is the reviewer's to date, not an event to log.
 */ export function waitingEvents(rules, duties, checkCodes) {
    const byKind = new Map();
    for (const r of rules){
        const k = r.eventKind;
        if (!k || NOT_LOGGED_HERE.has(k) || k === 'receipt_of_order') continue;
        if (!checkCodes[r.dutyId]?.codes.includes('waiting_on_event')) continue;
        byKind.set(k, (byKind.get(k) ?? new Set()).add(r.dutyId));
    }
    const named = new Map(duties.filter((d)=>d.eventKind).map((d)=>[
            d.eventKind,
            d.title
        ]));
    return [
        ...byKind
    ].map(([kind, ids])=>({
            kind,
            label: eventLabel(kind, named.get(kind)),
            dutyIds: [
                ...ids
            ]
        })).sort((a, b)=>a.label.localeCompare(b.label));
}
/** "Kickoff meeting" for kickoff_meeting; `title` (the dating deliverable) wins. */ export function eventLabel(kind, title) {
    if (title) return title;
    const known = EVENT_KINDS.find((k)=>k.value === kind)?.label;
    if (known) return known;
    const words = kind.replace(/_/g, ' ');
    return words ? words[0].toUpperCase() + words.slice(1) : '';
}
const CADENCE_WORD = {
    daily: 'daily',
    weekly: 'weekly',
    biweekly: 'every two weeks',
    semimonthly: 'twice a month',
    monthly: 'monthly',
    quarterly: 'quarterly',
    semiannually: 'twice a year',
    annually: 'yearly'
};
const UNIT_WORD = {
    calendar_day: [
        'day',
        'days'
    ],
    business_day: [
        'business day',
        'business days'
    ],
    week: [
        'week',
        'weeks'
    ],
    month: [
        'month',
        'months'
    ]
};
const EVENT_WORD = {
    receipt_of_order: 'receipt of order',
    exercise_start: 'each exercise starts',
    option_period_start: 'each option period starts',
    award: 'the award',
    modification_effective: 'a modification takes effect',
    on_request: 'each government request'
};
function anchorWord(gate) {
    switch(gate){
        case 'optional_task':
            return "each exercise's PoP start";
        case 'option_period':
            return "the option period's start";
        case 'base':
            return "the base period's start";
        case 'sow':
            return "the SOW's start";
        default:
            return 'the start';
    }
}
function endWord(gate) {
    switch(gate){
        case 'optional_task':
            return "until that exercise's PoP end";
        case 'option_period':
            return 'until the option period ends';
        case 'base':
            return 'until the base period ends';
        case 'sow':
            return 'while the SOW is in force';
        default:
            return '';
    }
}
function offsetPhrase(r, anchor) {
    if (!r.hasOffset) return anchor;
    const n = r.offsetN;
    const [one, many] = UNIT_WORD[r.offsetUnit] ?? [
        'day',
        'days'
    ];
    if (n === 0) return `on ${anchor}`;
    const abs = Math.abs(n);
    return `${abs} ${abs === 1 ? one : many} ${n < 0 ? 'before' : 'after'} ${anchor}`;
}
function cadencePhrase(r) {
    const word = CADENCE_WORD[r.cadence] ?? r.cadence;
    if (r.cadenceInterval > 1) {
        if (r.cadence === 'weekly') return `every ${r.cadenceInterval} weeks`;
        if (r.cadence === 'monthly') return `every ${r.cadenceInterval} months`;
        if (r.cadence === 'annually') return `every ${r.cadenceInterval} years`;
    }
    return word;
}
function dayPhrase(r) {
    if (r.dayWeekday) return `, on ${r.dayWeekday[0].toUpperCase()}${r.dayWeekday.slice(1)}`;
    if (r.dayOrdinals.length > 0) {
        const days = r.dayOrdinals.map((d)=>d === -1 ? 'the last day' : `day ${d}`).join(' and ');
        return `, on ${days}${r.dayBasis === 'business_day' ? ' (business days)' : ''}`;
    }
    return '';
}
/**
 * "7 days after each exercise's PoP start, then monthly, until that
 * exercise's PoP end". A rule with no first date reads as such, so the reading
 * itself shows why no dates exist.
 */ export function describeRule(r) {
    const anchor = r.eventKind ? EVENT_WORD[r.eventKind] ?? r.eventKind.replace(/_/g, ' ') : anchorWord(r.gate);
    let text;
    if (r.kind === 'dated') {
        text = r.dueOn ? `Once, on ${cmDate(r.dueOn)}` : `Once, ${offsetPhrase(r, anchor)}`;
        if (!r.dueOn && !r.hasOffset) text = 'Once, date not stated';
    } else if (r.kind === 'event') {
        text = `${offsetPhrase(r, anchor)}`;
        text = text[0].toUpperCase() + text.slice(1);
    } else {
        const first = r.dueOn ? `From ${cmDate(r.dueOn)}` : r.hasOffset ? offsetPhrase(r, anchor) : r.dayWeekday || r.dayOrdinals.length > 0 ? `From ${anchor}` : 'No first date stated';
        text = `${first[0].toUpperCase()}${first.slice(1)}, then ${cadencePhrase(r)}${dayPhrase(r)}`;
        const end = r.eventKind ? '' : endWord(r.gate);
        if (end) text += `, ${end}`;
    }
    if (r.effectiveFrom || r.effectiveTo) {
        text += ` (in effect ${r.effectiveFrom ? `from ${cmDate(r.effectiveFrom)}` : ''}${r.effectiveFrom && r.effectiveTo ? ' ' : ''}${r.effectiveTo ? `until ${cmDate(r.effectiveTo)}` : ''})`;
    }
    return text;
}
/** A recurring or dated rule with no stated date, no offset and no day rule has no first date. */ export function ruleLacksStart(r) {
    if (r.kind === 'event') return false;
    if (r.dueOn || r.hasOffset) return false;
    if (r.kind === 'recurring' && (r.dayWeekday || r.dayOrdinals.length > 0)) return false;
    return true;
}
/** The mint receipt: "12 dates added", "10 retired, 3 added", "No dates changed". */ export function mintReceipt(m) {
    if (!m) return 'Saved';
    const parts = [];
    const n = (x, w)=>`${x} ${x === 1 ? 'date' : 'dates'} ${w}`;
    if (m.retired) parts.push(n(m.retired, 'retired'));
    if (m.inserted) parts.push(n(m.inserted, 'added'));
    if (m.updated) parts.push(n(m.updated, 'moved'));
    if (m.revived) parts.push(n(m.revived, 'restored'));
    return parts.length ? parts.join(', ') : 'No dates changed';
}
